import { BaseModel } from '@shared/models/entity/base/base.model';
import { QueryPagination } from '@shared/types/query-options.types';
import {
  Channel,
  Building,
  Contact,
  DatabaseFile,
  Property,
  Unit,
} from '../../src/app/shared/models/entity';
import { Dataset, createDataset } from './seed.fixture';

// -----------------------------------------------------------------------------------------------------
// @ TYPES
// -----------------------------------------------------------------------------------------------------

export interface MockQuery<TModel> {
  /** A flat filter object, mirroring the entity's `*Filter` model. */
  filter?: Partial<Record<keyof TModel | 'filter', unknown>>;
  /** A sort object, mirroring the entity's `*Sort` model. */
  sorting?: Partial<Record<keyof TModel, 'asc' | 'desc'>>;
  /** Mirrors `QueryPagination` — 1-based page, `pageSize` items per page. */
  pagination?: QueryPagination;
}

export interface MockPage<TModel> {
  total: number;
  items: TModel[];
}

/** A custom predicate for a filter key the generic matcher can't express. */
export type MockPredicate<TModel> = (item: TModel, value: unknown) => boolean;

export interface MockStoreOptions<TModel> {
  /** The fields a free-text `filter` term is matched against. */
  searchFields?: (keyof TModel)[];
  /** Per-filter-key overrides for anything the generic matcher gets wrong. */
  predicates?: Partial<Record<string, MockPredicate<TModel>>>;
}

// -----------------------------------------------------------------------------------------------------
// @ STORE
// -----------------------------------------------------------------------------------------------------

/**
 * An in-memory collection standing in for one backend entity endpoint.
 *
 * It speaks the same language as `EntityService`: filter, sorting, pagination, total — so a mock
 * service can hand its options bag straight through, and swapping the mock out for the real HTTP
 * call later is a one-line change in the service.
 */
export class MockEntityStore<TModel extends BaseModel> {
  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  private readonly items = new Map<string, TModel>();

  private readonly options: MockStoreOptions<TModel>;

  private readonly factory: (init: Partial<TModel>) => TModel;

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(
    factory: (init: Partial<TModel>) => TModel,
    seed: TModel[] = [],
    options: MockStoreOptions<TModel> = {},
  ) {
    this.factory = factory;
    this.options = options;

    this.reset(seed);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Runs a query against the collection: filter, then sort, then paginate.
   *
   * @param query - The filter, sorting and pagination to apply.
   * @returns {MockPage<TModel>} The matching page and the total count before pagination.
   */
  query(query: MockQuery<TModel> = {}): MockPage<TModel> {
    let items: TModel[] = [...this.items.values()].filter((item: TModel) =>
      this.matches(item, query.filter),
    );

    items = this.sort(items, query.sorting);

    const total: number = items.length;

    if (query.pagination) {
      const { page, pageSize } = query.pagination;
      const start: number = Math.max(0, (Math.max(1, page) - 1) * pageSize);

      items = items.slice(start, start + pageSize);
    }

    return { total, items };
  }

  /**
   * Returns a single entity by id.
   *
   * @param id - The id of the entity.
   * @returns {TModel | null} The entity, or null if it doesn't exist.
   */
  getById(id: string): TModel | null {
    return this.items.get(id) ?? null;
  }

  /**
   * Creates an entity and stores it.
   *
   * @param init - The fields of the new entity.
   * @returns {TModel} The created entity.
   */
  create(init: Partial<TModel>): TModel {
    const entity: TModel = this.factory({
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      ...init,
    } as Partial<TModel>);

    this.items.set(entity.id, entity);

    return entity;
  }

  /**
   * Applies a partial update to an entity.
   *
   * @param id - The id of the entity.
   * @param changes - The fields to update.
   * @returns {TModel | null} The updated entity, or null if it doesn't exist.
   */
  update(id: string, changes: Partial<TModel>): TModel | null {
    const current: TModel | null = this.getById(id);

    if (current === null) {
      return null;
    }

    const updated: TModel = this.factory({
      ...current,
      ...changes,
      id,
      modifiedAt: new Date().toISOString(),
    } as Partial<TModel>);

    this.items.set(id, updated);

    return updated;
  }

  /**
   * Removes an entity from the collection.
   *
   * @param id - The id of the entity.
   * @returns {boolean} True if the entity existed and was removed.
   */
  deleteById(id: string): boolean {
    return this.items.delete(id);
  }

  /**
   * Replaces the whole collection — call this in `beforeEach` so tests never leak state into each other.
   *
   * @param seed - The entities to seed the collection with.
   * @returns {void}
   */
  reset(seed: TModel[] = []): void {
    this.items.clear();

    for (const item of seed) {
      this.items.set(item.id, item);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Matches an entity against a flat filter object.
   * A custom predicate wins; then the free-text `filter` term; then array-contains; then equality.
   *
   * @param item - The entity to test.
   * @param filter - The filter to apply.
   * @returns {boolean} True if the entity matches every criterion.
   */
  private matches(item: TModel, filter?: MockQuery<TModel>['filter']): boolean {
    if (!filter) {
      return true;
    }

    return Object.entries(filter).every(([key, value]: [string, unknown]) => {
      if (value === null || value === undefined || value === '') {
        return true;
      }

      const predicate: MockPredicate<TModel> | undefined = this.options.predicates?.[key];

      if (predicate) {
        return predicate(item, value);
      }

      if (key === 'filter') {
        return this.matchesSearchTerm(item, String(value));
      }

      const field: unknown = item[key as keyof TModel];

      if (Array.isArray(value)) {
        return value.some((candidate: unknown) => String(candidate) === String(field));
      }

      return String(field) === String(value);
    });
  }

  /**
   * Matches an entity against a free-text search term, across the configured search fields.
   *
   * @param item - The entity to test.
   * @param term - The search term.
   * @returns {boolean} True if any search field contains the term.
   */
  private matchesSearchTerm(item: TModel, term: string): boolean {
    const fields: (keyof TModel)[] = this.options.searchFields ?? [];
    const needle: string = term.toLowerCase();

    return fields.some((field: keyof TModel) =>
      String(item[field] ?? '')
        .toLowerCase()
        .includes(needle),
    );
  }

  /**
   * Sorts a list by the given sort object. Multiple keys are applied in declaration order.
   *
   * @param items - The items to sort.
   * @param sorting - The sort object.
   * @returns {TModel[]} A new, sorted array.
   */
  private sort(items: TModel[], sorting?: MockQuery<TModel>['sorting']): TModel[] {
    if (!sorting) {
      return items;
    }

    const keys = Object.entries(sorting) as [keyof TModel, 'asc' | 'desc'][];

    return [...items].sort((left: TModel, right: TModel) => {
      for (const [key, direction] of keys) {
        const a: unknown = left[key];
        const b: unknown = right[key];

        if (a === b) {
          continue;
        }

        const comparison: number =
          typeof a === 'number' && typeof b === 'number'
            ? a - b
            : String(a).localeCompare(String(b));

        return direction === 'desc' ? -comparison : comparison;
      }

      return 0;
    });
  }
}

// -----------------------------------------------------------------------------------------------------
// @ DATABASE
// -----------------------------------------------------------------------------------------------------

/**
 * The mock database: one store per entity, seeded from a single coherent dataset so ids and
 * denormalized names line up across every endpoint.
 */
export interface MockDatabase {
  properties: MockEntityStore<Property>;
  buildings: MockEntityStore<Building>;
  units: MockEntityStore<Unit>;
  contacts: MockEntityStore<Contact>;
  files: MockEntityStore<DatabaseFile>;
  channels: MockEntityStore<Channel>;
  dataset: Dataset;
}

/**
 * Builds the mock database from a seeded dataset.
 *
 * @param dataset - The dataset to seed from. Defaults to the standard portfolio.
 * @returns {MockDatabase} The seeded stores.
 */
export function createMockDatabase(dataset: Dataset = createDataset()): MockDatabase {
  return {
    dataset,
    properties: new MockEntityStore<Property>((init) => new Property(init), dataset.properties, {
      searchFields: ['name', 'city', 'street', 'branchName'],
    }),
    buildings: new MockEntityStore<Building>((init) => new Building(init), dataset.buildings, {
      searchFields: ['name', 'propertyName', 'street'],
    }),
    units: new MockEntityStore<Unit>((init) => new Unit(init), dataset.units, {
      searchFields: ['name', 'number', 'currentOwnerName', 'buildingName', 'propertyName'],
    }),
    contacts: new MockEntityStore<Contact>((init) => new Contact(init), dataset.contacts, {
      searchFields: ['name', 'email', 'phoneNumber', 'city'],
    }),
    files: new MockEntityStore<DatabaseFile>((init) => new DatabaseFile(init), dataset.files, {
      searchFields: ['name', 'extension', 'contentType', 'fileType'],
      predicates: {
        // `tags` and `allowedUsages` are string arrays — a filter value must be *contained*,
        // not equal, so the generic matcher would get these wrong.
        tags: (file: DatabaseFile, value: unknown) =>
          toArray(value).every((tag) => file.tags?.includes(tag)),
        allowedUsages: (file: DatabaseFile, value: unknown) =>
          toArray(value).every((usage) => file.allowedUsages?.includes(usage)),
      },
    }),
    channels: new MockEntityStore<Channel>((init) => new Channel(init), dataset.channels, {
      searchFields: ['name' as keyof Channel, 'propertyName' as keyof Channel],
    }),
  };
}

/**
 * Normalizes a filter value into a string array, so array-valued filters can be matched uniformly.
 *
 * @param value - The raw filter value.
 * @returns {string[]} The value as a string array.
 */
function toArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item: unknown) => String(item));
  }

  return [String(value)];
}
